import { ReactNode, useId } from "react";
import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { Button, ButtonVariant, CancelButton } from "./Button";
import { DialogSize, Modal, ModalHeight } from "./Dialog";

export type UploadModalMessageType = "error" | "info" | "success";

export interface UploadModalMessage {
  id?: number | string;
  text: ReactNode;
  type: UploadModalMessageType;
}

export interface UploadModalProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actionLabel: ReactNode;
  onAction: () => void | Promise<void>;
  prompt?: ReactNode;
  fieldLabel?: ReactNode;
  supplementaryContent?: ReactNode;
  message?: UploadModalMessage | null;
  actionIntent?: Extract<ButtonVariant, "primary" | "success" | "ai">;
  actionIcon?: ReactNode;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  allowCancelWhileLoading?: boolean;
  showCancelAction?: boolean;
  size?: DialogSize;
  height?: ModalHeight;
  presentation?: "simple" | "action";
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  compactContentSpacing?: boolean;
}

const UploadContent = styled.div<{
  $presentation: "simple" | "action";
  $compactContentSpacing: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ $presentation, $compactContentSpacing }) =>
    $compactContentSpacing
      ? vars.spacing.lg
      : $presentation === "action"
        ? vars.spacing["3xl"]
        : vars.spacing.lg};
`;

const UploadPrompt = styled.p<{ $presentation: "simple" | "action" }>`
  margin: 0;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${({ $presentation }) =>
    $presentation === "simple"
      ? vars.typography.size.xl
      : vars.typography.size.md};
  font-weight: ${({ $presentation }) =>
    $presentation === "simple"
      ? vars.typography.weight.bold
      : vars.typography.weight.regular};
  line-height: ${({ $presentation }) =>
    $presentation === "simple"
      ? vars.typography.lineHeight.xl
      : vars.typography.lineHeight.md};
`;

const UploadField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vars.spacing.lg};
`;

const UploadFieldLabel = styled.div`
  color: ${vars.color.text.secondary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.md};
  font-weight: ${vars.typography.weight.medium};
  line-height: ${vars.typography.lineHeight.md};
`;

const SimpleActionWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const SimpleUploadAction = styled(Button).attrs({ size: "lg" })`
  width: 250px;
  height: 48px;
  border-radius: ${vars.radius.lg};
  font-size: ${vars.typography.size.lg};
`;

const ActionUploadButton = styled(Button).attrs({ size: "sm" })`
  width: 170px;
`;

const ActionUploadCancelButton = styled(CancelButton).attrs({ size: "sm" })`
  width: 170px;
`;

const UploadMessage = styled.div<{ $type: UploadModalMessageType }>`
  padding: ${vars.spacing.sm} ${vars.spacing.md};
  border: ${vars.borderWidth.thin} solid
    ${({ $type }) =>
      $type === "error" ? vars.color.border.danger : vars.color.border.medium};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.subtle};
  color: ${({ $type }) =>
    $type === "error" ? vars.color.text.danger : vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.sm};
`;

export const UploadModal = ({
  open,
  title,
  onClose,
  children,
  actionLabel,
  onAction,
  prompt,
  fieldLabel,
  supplementaryContent,
  message,
  actionIntent = "primary",
  actionIcon,
  actionLoading = false,
  actionDisabled = false,
  cancelLabel = "Cancel",
  cancelDisabled = false,
  allowCancelWhileLoading = false,
  showCancelAction,
  size,
  height = "content",
  presentation = "simple",
  closeOnEscape = false,
  closeOnOverlayClick = true,
  compactContentSpacing = false,
}: UploadModalProps) => {
  const generatedDescriptionId = useId();
  const descriptionId = prompt
    ? `inzeloop-upload-description-${generatedDescriptionId.replace(/:/g, "")}`
    : undefined;
  const hasCancelAction = showCancelAction ?? presentation === "action";
  const resolvedSize = size ?? (presentation === "simple" ? "sm" : "xl");

  const action =
    presentation === "simple" ? (
      <SimpleUploadAction
        variant={actionIntent}
        leadingIcon={actionIcon}
        loading={actionLoading}
        loadingLabel="Processing"
        disabled={actionDisabled}
        onClick={onAction}
      >
        {actionLabel}
      </SimpleUploadAction>
    ) : (
      <ActionUploadButton
        variant={actionIntent}
        leadingIcon={actionIcon}
        loading={actionLoading}
        loadingLabel="Processing"
        disabled={actionDisabled}
        onClick={onAction}
      >
        {actionLabel}
      </ActionUploadButton>
    );

  return (
    <Modal
      open={open}
      title={title}
      closeLabel="Close upload dialog"
      onClose={onClose}
      size={resolvedSize}
      height={height}
      intent={presentation === "simple" ? "upload" : "action"}
      showHeader={presentation === "action"}
      showCloseButton={presentation === "action"}
      descriptionId={descriptionId}
      closeOnEscape={closeOnEscape}
      closeOnOverlayClick={closeOnOverlayClick}
      footer={
        presentation === "action" ? (
          <>
            {hasCancelAction && (
              <ActionUploadCancelButton
                onClick={onClose}
                disabled={
                  cancelDisabled || (actionLoading && !allowCancelWhileLoading)
                }
              >
                {cancelLabel}
              </ActionUploadCancelButton>
            )}
            {action}
          </>
        ) : undefined
      }
    >
      <UploadContent
        $presentation={presentation}
        $compactContentSpacing={compactContentSpacing}
      >
        {prompt && (
          <UploadPrompt id={descriptionId} $presentation={presentation}>
            {prompt}
          </UploadPrompt>
        )}
        <UploadField>
          {fieldLabel && <UploadFieldLabel>{fieldLabel}</UploadFieldLabel>}
          {children}
        </UploadField>
        {supplementaryContent}
        {presentation === "simple" && (
          <SimpleActionWrapper>{action}</SimpleActionWrapper>
        )}
        {message && (
          <UploadMessage
            key={message.id}
            $type={message.type}
            role={message.type === "error" ? "alert" : "status"}
            aria-live={message.type === "error" ? "assertive" : "polite"}
          >
            {message.text}
          </UploadMessage>
        )}
      </UploadContent>
    </Modal>
  );
};
